/**
 * CollaborationIntegrationBridge - Integration with existing collaboration system
 * 
 * Connects the WebAssembly Client-First typography system with the existing
 * EdgeLex collaboration infrastructure without duplicating functionality.
 */

import { LocalDocumentEngine } from '../core/LocalDocumentEngine';
import { WsVersion1EdgedocsWsClient } from '../../Websockets/wsVersion1EdgedocsWsClient';

// Minimal renderer interface for collaboration
interface MinimalRenderer {
  getCanvas(): HTMLCanvasElement;
  getContext(): CanvasRenderingContext2D;
  requestRender(): void;
  getCharacterPosition?(index: number): any;
  setDocumentState?(state: any): void;
  setBackendLayoutPlan?(plan: any): void;
  updateRemoteCursor?(userId: string, position: any): void;
  removeRemoteCursor?(userId: string): void;
}
import type { Operation } from '../types/DocumentTypes';

export interface CollaborationConfig {
  documentId: string;
  versionId?: string;
  websocketUrl: string;
  token: string;
  realmId: string;
  attorney: {
    id: string;
    name: string;
    email: string;
  };
}

export class CollaborationIntegrationBridge {
  private documentEngine: LocalDocumentEngine;
  private canvasRenderer: MinimalRenderer;
  private wsClient: WsVersion1EdgedocsWsClient | null = null;
  private config: CollaborationConfig | null = null;
  private sessionReady: boolean = false;
  private isConnected: boolean = false;
  private baselineAcked: boolean = false;
  private pendingDeltas: Array<{
    operation: string;
    position: { start: number; end: number };
    content: string;
    operation_id: string;
    requires_approval: boolean;
    attorney: any;
    timestamp: Date;
  }> = [];

  // Option D: No client-side debouncing - immediate server sync for best UX

  constructor(documentEngine: LocalDocumentEngine, canvasRenderer: MinimalRenderer) {
    console.log('🏗️ [CollaborationIntegrationBridge] Constructor called', {
      hasDocumentEngine: !!documentEngine,
      hasRenderer: !!canvasRenderer,
      sessionReady: this.sessionReady,
      baselineAcked: this.baselineAcked
    });
    
    this.documentEngine = documentEngine;
    this.canvasRenderer = canvasRenderer;
    
    console.log('🔧 [CollaborationIntegrationBridge] Setting up document engine integration in constructor...');
    this.setupDocumentEngineIntegration();
    
    console.log('✅ [CollaborationIntegrationBridge] Constructor completed');
  }

  /**
   * Initialize collaboration with existing WebSocket system
   */
  private isInitialized = false;
  
  async initialize(config: CollaborationConfig): Promise<void> {
    if (this.isInitialized) {
      console.warn('🚨 [CollaborationIntegrationBridge] PREVENTING DUPLICATE INITIALIZATION - already initialized');
      return;
    }
    
    this.isInitialized = true;
    console.error('🚨🚨🚨 [CollaborationIntegrationBridge] INITIALIZE CALLED - THIS SHOULD BE VISIBLE! 🚨🚨🚨');
    console.error('🚨 [CollaborationIntegrationBridge] Config received:', {
      documentId: config.documentId,
      versionId: config.versionId,
      hasWebsocketUrl: !!config.websocketUrl,
      hasToken: !!config.token
    });
    
    this.config = config;
    
    try {
      console.log('🚀 [CollaborationIntegrationBridge] STARTING collaboration initialization...', {
        websocketUrl: config.websocketUrl,
        documentId: config.documentId,
        versionId: config.versionId,
        versionIdType: typeof config.versionId,
        versionIdIsNull: config.versionId === null,
        versionIdIsUndefined: config.versionId === undefined,
        realmId: config.realmId,
        attorneyId: config.attorney.id
      });
      
      // Initialize existing WebSocket client
      this.wsClient = new WsVersion1EdgedocsWsClient(config.websocketUrl, {
        token: config.token,
        realmId: config.realmId
      });
      
      console.log('🔌 [CollaborationIntegrationBridge] WebSocket client created, setting up handlers...');
      
      // Set up WebSocket event handlers
      this.setupWebSocketHandlers();
      
      console.log('🔗 [CollaborationIntegrationBridge] Connecting to WebSocket...');
      
      // Connect to collaboration namespace and wait for connection
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.error('⏰ [CollaborationIntegrationBridge] WebSocket connection TIMEOUT after 10 seconds');
          reject(new Error('WebSocket connection timeout'));
        }, 10000); // 10 second timeout
        
        this.wsClient!.onOpen(() => {
          clearTimeout(timeout);
          console.log('✅ [CollaborationIntegrationBridge] WebSocket connected successfully');
          resolve();
        });
        
        this.wsClient!.onError((error: any) => {
          clearTimeout(timeout);
          console.error('❌ [CollaborationIntegrationBridge] WebSocket connection error:', error);
          reject(error);
        });
        
        this.wsClient!.connect();
      });
      
      console.log('🏠 [CollaborationIntegrationBridge] WebSocket connected, joining document room...');
      
      // Now join document collaboration room after connection is established
      await this.joinDocumentRoom();
      
      console.log('🎉 [CollaborationIntegrationBridge] Collaboration integration initialized successfully');
      
    } catch (error) {
      console.error('💥 [CollaborationIntegrationBridge] Initialization FAILED:', error);
      throw error;
    }
  }

  /**
   * Set up document engine integration
   */
  private setupDocumentEngineIntegration(): void {
    console.log('🔧 [CollaborationIntegrationBridge] Setting up document engine integration...');
    
    // Listen for document content changes and send to collaboration system
    this.documentEngine.on('documentContentChanged', (eventData: any) => {
      console.log('📝 [CollaborationIntegrationBridge] Document content changed event received:', {
        operation: eventData.operation,
        position: eventData.position,
        operationId: eventData.operation_id,
        sessionReady: this.sessionReady,
        baselineAcked: this.baselineAcked
      });
      this.handleDocumentContentChange(eventData);
    });
    
    // Listen for layout updates to sync cursor positions
    this.documentEngine.on('layoutUpdated', (eventData: any) => {
      console.log('📐 [CollaborationIntegrationBridge] Layout updated event received');
      this.handleLayoutUpdate(eventData);
    });
    
    console.log('✅ [CollaborationIntegrationBridge] Document engine integration setup complete');
  }

  /**
   * Set up WebSocket event handlers for existing collaboration system
   */
  private setupWebSocketHandlers(): void {
    if (!this.wsClient) {
      console.error('🚨 [CollaborationIntegrationBridge] Cannot setup handlers - wsClient is null');
      return;
    }
    
    console.log('🎧 [CollaborationIntegrationBridge] Setting up WebSocket event handlers...');
    
    // Handle connection events
    this.wsClient.onOpen(() => {
      console.log('🔗 [CollaborationIntegrationBridge] WebSocket connected');
      this.isConnected = true;
    });
    
    this.wsClient.onClose(() => {
      console.log('🔌 [CollaborationIntegrationBridge] WebSocket disconnected');
      this.isConnected = false;
      this.sessionReady = false;
      this.baselineAcked = false;
    });
    
    this.wsClient.onError((error: any) => {
      console.error('❌ [CollaborationIntegrationBridge] WebSocket error:', error);
      this.sessionReady = false;
      this.baselineAcked = false;
    });
    
    // Handle collaboration events
    this.wsClient
      .onInitial((eventData: any) => {
        try {
          console.log('🎯 [CollaborationIntegrationBridge] INITIAL EVENT RECEIVED!', {
            hasData: !!eventData?.data,
            serverVersion: eventData?.data?.serverVersion ?? eventData?.serverVersion,
            eventType: eventData?.event_type || eventData?.type,
            fullEventData: eventData
          });
          
          // Mark baseline acknowledged when initial state arrives
          this.baselineAcked = true;
          console.log('✅ [CollaborationIntegrationBridge] baselineAcked set to TRUE');
          
          // Initial may carry server version; persist into document engine state if provided
          const serverVersion = eventData?.data?.serverVersion ?? eventData?.serverVersion ?? 0;
          try { (this.documentEngine as any)?.setServerVersion?.(serverVersion); } catch {}
          
          console.log('🚀 [CollaborationIntegrationBridge] Baseline acknowledged - marking session ready for deltas');
          
          // Only after receiving initial do we mark the session as ready for deltas
          this.markSessionReady();
          this.handleCollaborationEvent(eventData);
        } catch (error) {
          console.error('💥 [CollaborationIntegrationBridge] Error handling initial event:', error);
        }
      })
      .onUpdate((eventData: any) => {
        console.log('📨 [CollaborationIntegrationBridge] Update event received:', eventData);
        this.handleCollaborationEvent(eventData);
      })
      .onOpAck((msg: any) => {
        console.log('✅ [CollaborationIntegrationBridge] Operation acknowledged:', msg?.opId);
        // Future: correlate opId with local queue if needed
      })
      .onOpReject((msg: any) => {
        console.warn('❌ [CollaborationIntegrationBridge] Server rejected op:', {
          reason: msg?.reason,
          opId: msg?.opId,
          sessionReady: this.sessionReady,
          baselineAcked: this.baselineAcked
        });
      });

    // Watch for initial/baseline acknowledgement from server
    // Server should include a type or flag; we use 'initial' path here
    // The ws client already routes 'edgedocs:initial' through onUpdate; mark ready on first initial
    if (typeof window !== 'undefined') {
      window.addEventListener('edgedocs-session-ready', () => {/* no-op, already ready */});
    }
  }

  /**
   * Join document collaboration room using existing API
   */
  private async joinDocumentRoom(): Promise<void> {
    if (!this.wsClient || !this.config) {
      console.error('🚨 [CollaborationIntegrationBridge] Cannot join room - missing wsClient or config');
      return;
    }
    
    try {
      console.log('🏠 [CollaborationIntegrationBridge] Joining document room...', {
        documentId: this.config.documentId,
        isConnected: this.isConnected,
        wsClientConnected: this.wsClient.connected
      });
      
      // Send join-document event using existing WebSocket protocol
      // Use version-specific join if versionId is provided
      console.log('🎯 [CollaborationIntegrationBridge] VERSION CHECK:', {
        hasVersionId: !!this.config.versionId,
        versionId: this.config.versionId,
        versionIdType: typeof this.config.versionId,
        versionIdLength: this.config.versionId?.length,
        willJoinVersionRoom: !!this.config.versionId
      });
      
      if (this.config.versionId) {
        console.log(`🔗 [CollaborationIntegrationBridge] ✅ JOINING VERSION-SPECIFIC ROOM for document ${this.config.documentId}, version ${this.config.versionId}`);
        this.wsClient.joinVersion(this.config.documentId, this.config.versionId);
      } else {
        console.log(`🔗 [CollaborationIntegrationBridge] ❌ JOINING DOCUMENT-LEVEL ROOM for document ${this.config.documentId} (NO VERSION ISOLATION)`);
        this.wsClient.join(this.config.documentId);
      }
      console.log('📨 [CollaborationIntegrationBridge] Join event sent');
      
      // Initialize document session (this creates the backend session)
      console.log('🔄 [CollaborationIntegrationBridge] Requesting initial state...');
      if (this.config.versionId) {
        this.wsClient.getInitialVersion(this.config.documentId, this.config.versionId);
      } else {
        this.wsClient.getInitial(this.config.documentId);
      }
      console.log('📨 [CollaborationIntegrationBridge] Initial state request sent');
      
      console.log(`✅ [CollaborationIntegrationBridge] Document room join sequence completed for: ${this.config.documentId}`);
      
    } catch (error) {
      console.error('💥 [CollaborationIntegrationBridge] Failed to join document room:', error);
    }
  }

  /**
   * Mark collaboration session as ready and emit a global event
   */
  private markSessionReady(): void {
    console.log('🚀 [CollaborationIntegrationBridge] MARKING SESSION READY!', {
      previousSessionReady: this.sessionReady,
      baselineAcked: this.baselineAcked,
      pendingDeltasCount: this.pendingDeltas.length
    });
    
    this.sessionReady = true;
    
    // Process any queued deltas now that session is ready
    console.log('📤 [CollaborationIntegrationBridge] Processing pending deltas...');
    this.processPendingDeltas();
    
    try {
      const evt = new CustomEvent('edgedocs-session-ready', { detail: { documentId: this.config?.documentId } });
      window.dispatchEvent(evt);
      console.log('📡 [CollaborationIntegrationBridge] Session ready event dispatched');
    } catch (error) {
      console.error('❌ [CollaborationIntegrationBridge] Failed to dispatch session ready event:', error);
    }
  }

  /**
   * Queue a delta for later sending when session is ready
   */
  private queuePendingDelta(eventData: {
    operation: string;
    position: { start: number; end: number };
    content: string;
    operation_id: string;
    requires_approval: boolean;
    attorney: any;
    timestamp: Date;
  }): void {
    // Limit queue size to prevent memory issues
    if (this.pendingDeltas.length >= 50) {
      console.warn('[CollaborationIntegrationBridge] Pending delta queue full, dropping oldest delta');
      this.pendingDeltas.shift();
    }
    
    this.pendingDeltas.push(eventData);
    console.log(`[CollaborationIntegrationBridge] Queued delta ${eventData.operation_id}, queue size: ${this.pendingDeltas.length}`);
  }

  /**
   * Process all pending deltas when session becomes ready
   */
  private processPendingDeltas(): void {
    if (this.pendingDeltas.length === 0) {
      console.log('📭 [CollaborationIntegrationBridge] No pending deltas to process');
      return;
    }
    
    console.log(`📤 [CollaborationIntegrationBridge] Processing ${this.pendingDeltas.length} pending deltas`);
    
    // Process deltas in order
    const deltas = [...this.pendingDeltas];
    this.pendingDeltas = []; // Clear queue
    
    for (const delta of deltas) {
      // Re-check session state before sending each delta
      if (this.sessionReady && this.baselineAcked) {
        console.log('📨 [CollaborationIntegrationBridge] Sending queued delta:', delta.operation_id);
        this.sendDeltaToServer(delta);
      } else {
        console.warn('⚠️ [CollaborationIntegrationBridge] Session became unready during pending delta processing');
        // Re-queue remaining deltas
        this.pendingDeltas.unshift(delta);
        break;
      }
    }
    
    console.log(`✅ [CollaborationIntegrationBridge] Finished processing pending deltas. Remaining: ${this.pendingDeltas.length}`);
  }

  /**
   * Expose readiness state
   */
  public isSessionReady(): boolean { return this.sessionReady; }

  /**
   * Get detailed collaboration status for debugging
   */
  public getCollaborationStatus(): {
    isConnected: boolean;
    sessionReady: boolean;
    baselineAcked: boolean;
    pendingDeltasCount: number;
    hasWsClient: boolean;
    hasConfig: boolean;
  } {
    return {
      isConnected: this.isConnected,
      sessionReady: this.sessionReady,
      baselineAcked: this.baselineAcked,
      pendingDeltasCount: this.pendingDeltas.length,
      hasWsClient: !!this.wsClient,
      hasConfig: !!this.config
    };
  }

  /**
   * Handle document content changes and send to collaboration system
   * Option D: Immediate server sync for smooth typing experience
   */
  private handleDocumentContentChange(eventData: {
    operation: string;
    position: { start: number; end: number };
    content: string;
    operation_id: string;
    requires_approval: boolean;
    attorney: any;
    timestamp: Date;
  }): void {
    if (!this.wsClient || !this.config) return;
    
    // CRITICAL: Hard gate - NEVER send deltas until BOTH conditions are met
    if (!this.sessionReady || !this.baselineAcked) {
      console.warn('[CollaborationIntegrationBridge] 🚫 BLOCKING delta send - session not ready', {
        sessionReady: this.sessionReady,
        baselineAcked: this.baselineAcked,
        operation: eventData.operation,
        operationId: eventData.operation_id
      });
      
      // Queue the delta for later sending (after session is ready)
      this.queuePendingDelta(eventData);
      
      // Re-request initial to ensure session establishment
      try {
        console.log('[CollaborationIntegrationBridge] Re-requesting initial due to blocked delta');
        this.wsClient!.getInitial(this.config.documentId);
      } catch (error) {
        console.error('[CollaborationIntegrationBridge] Failed to re-request initial:', error);
      }
      return;
    }
    
    // Session is ready - send immediately for smooth typing (Option D)
    this.sendDeltaToServer(eventData);
  }


  /**
   * Transform ClientFirst operation format to EdgeDocs protocol format
   */
  private transformToEdgeDocsFormat(eventData: {
    operation: string;
    position: { start: number; end: number };
    content: string;
    operation_id: string;
    requires_approval: boolean;
    attorney: any;
    timestamp: Date;
  }): any {
    // Map ClientFirst operations to EdgeDocs operations
    const operationMap: Record<string, string> = {
      'insert': 'insertText',
      'delete': 'deleteRange',
      'replace': 'insertText', // Replace is delete + insert
      'format': 'setStyle'
    };
    
    const edgeDocsOp = operationMap[eventData.operation] || 'insertText';
    
    // Default to paragraph 1 if no specific block targeting
    const blockId = 'p1'; // TODO: Extract from position or document structure
    
    // Transform based on operation type
    switch (edgeDocsOp) {
      case 'insertText':
        return {
          op: 'insertText',
          blockId: blockId,
          at: eventData.position.start,
          text: eventData.content || ''
        };
        
      case 'deleteRange':
        return {
          op: 'deleteRange',
          blockId: blockId,
          from: eventData.position.start,
          to: eventData.position.end
        };
        
      case 'setStyle':
        return {
          op: 'setStyle',
          runId: `r1_0`, // TODO: Map to actual run ID
          patch: { /* style properties */ }
        };
        
      default:
        // Fallback to insertText
        return {
          op: 'insertText',
          blockId: blockId,
          at: eventData.position.start,
          text: eventData.content || ''
        };
    }
  }

  /**
   * Send delta to server (extracted for reuse)
   */
  private sendDeltaToServer(eventData: {
    operation: string;
    position: { start: number; end: number };
    content: string;
    operation_id: string;
    requires_approval: boolean;
    attorney: any;
    timestamp: Date;
  }): void {
    if (!this.wsClient || !this.config) return;
    
    try {
      // Attach client/server version hints for backend reconciliation
      let clientVersion: number | undefined = undefined;
      let serverVersion: number | undefined = undefined;
      try { 
        const state = (this.documentEngine as any)?.getState?.();
        clientVersion = state?.version;
        serverVersion = state?.serverVersion;
      } catch {}
      
      // 🔍 ENHANCED LOGGING: Capture full session state before sending
      console.log('🚀 [CollaborationIntegrationBridge] SENDING DELTA - Full Session State:', {
        operationId: eventData.operation_id,
        documentId: this.config.documentId,
        operationType: eventData.operation,
        content: eventData.content,
        position: eventData.position,
        clientVersion,
        serverVersion,
        sessionReady: this.sessionReady,
        baselineAcked: this.baselineAcked,
        wsClientConnected: !!this.wsClient,
        wsClientId: (this.wsClient as any)?.getSocketId?.() || 'unknown',
        pendingDeltasCount: this.pendingDeltas.length,
        timestamp: new Date().toISOString(),
        attorney: eventData.attorney?.name || 'unknown'
      });
      
      // Transform data to EdgeDocs protocol format
      const edgeDocsOperation = this.transformToEdgeDocsFormat(eventData);
      
      // Send document delta using correct EdgeDocs WebSocket protocol
      this.wsClient.sendDelta(this.config.documentId, edgeDocsOperation, eventData.operation_id, clientVersion);
      
      console.log(`📤 [CollaborationIntegrationBridge] ✅ Delta sent successfully: ${eventData.operation} at ${eventData.position.start} (clientVersion: ${clientVersion ?? 'n/a'}, opId: ${eventData.operation_id})`);
      
    } catch (error) {
      console.error('❌ [CollaborationIntegrationBridge] Failed to send delta:', error);
    }
  }

  /**
   * Handle layout updates and sync cursor positions
   */
  private handleLayoutUpdate(eventData: { layoutData: any; documentState: any }): void {
    if (!this.wsClient || !this.config) return;
    
    try {
      const caretPosition = eventData.documentState.caretPosition;
      
      // Get pixel position for cursor using professional typography
      const charPosition = this.canvasRenderer?.getCharacterPosition?.(caretPosition);
      
      if (charPosition) {
        // Send cursor update using existing WebSocket protocol
        this.wsClient.sendCursor(this.config.documentId, {
          offset: caretPosition,
          x: charPosition.x,
          y: charPosition.y,
          line: Math.floor(charPosition.y / 24),
          column: Math.floor(charPosition.x / 10)
        });
      }
      
    } catch (error) {
      console.warn('[CollaborationIntegrationBridge] Failed to sync cursor position:', error);
    }
  }

  /**
   * Handle EdgeDocs update events (client-first architecture)
   * Processes collaboration data while preserving client typesetter as primary renderer
   */
  private async handleEdgeDocsUpdate(eventData: any): Promise<void> {
    try {
      console.log('📐 [CollaborationIntegrationBridge] EdgeDocs update received:', {
        hasBlocksText: !!eventData.blocksText,
        hasLayoutPlan: !!eventData.layoutPlan,
        hasCaretAfter: !!eventData.caretAfter,
        planHash: eventData.planHash,
        version: eventData.version
      });

      // COLLABORATION: Process remote document content changes
      if (eventData.blocksText) {
        await this.processRemoteDocumentContent(eventData.blocksText);
      }

      // COLLABORATION: Process remote caret positions
      if (eventData.caretAfter) {
        this.processRemoteCaretPosition(eventData.caretAfter, eventData);
      }

      // HYBRID ARCHITECTURE: Process server layout data while maintaining collaboration sync
      console.log('📐 [CollaborationIntegrationBridge] PRESERVING server layout - using HarfBuzz typography with collaboration');
      
      // Process server layout plan if available (HarfBuzz typography benefits)
      if (eventData.layoutPlan) {
        console.log('🔤 [HARFBUZZ_INTEGRATION] Server layout plan received - applying HarfBuzz typography');
        this.processServerLayoutPlan(eventData.layoutPlan);
      }

    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error handling EdgeDocs update:', error);
    }
  }

  /**
   * Process remote document content changes for collaboration
   */
  private async processRemoteDocumentContent(blocksText: any): Promise<void> {
    try {
      // Extract text content from blocks
      const remoteText = this.extractTextFromBlocks(blocksText);
      const currentText = this.documentEngine.getText();

      if (remoteText !== currentText) {
        console.log('🔄 [CollaborationIntegrationBridge] Remote document content differs, updating local state');
        
        // Update document engine with remote content
        // This will trigger client typesetter re-rendering (client-first design)
        const newState = {
          ...this.documentEngine.getState(),
          text: remoteText,
          version: this.documentEngine.getState().version + 1,
          modified: new Date()
        };
        await this.documentEngine.loadDocument(newState);
        
        // Update canvas renderer with new document state
        this.canvasRenderer.setDocumentState?.(this.documentEngine.getState());
        this.canvasRenderer.requestRender();
      }
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error processing remote document content:', error);
    }
  }

  /**
   * Process remote caret positions for collaboration
   */
  private processRemoteCaretPosition(caretAfter: any, eventData?: any): void {
    try {
      if (caretAfter.blockId && typeof caretAfter.at === 'number') {
        console.log('👆 [CollaborationIntegrationBridge] Remote caret position received:', caretAfter);
        
        // FIX 1: Filter out your own cursor updates
        if (this.isOwnCursorUpdate(caretAfter, eventData)) {
          console.log('🚫 [CollaborationIntegrationBridge] Ignoring own cursor echo from server');
          return;
        }
        
        // FIX 2 & 3: Extract actual user ID and validate it's a real remote user
        const remoteUserId = this.extractRemoteUserId(eventData);
        if (!remoteUserId || this.isCurrentUser(remoteUserId)) {
          console.log('🚫 [CollaborationIntegrationBridge] No valid remote user for cursor display');
          return;
        }
        
        console.log('✅ [CollaborationIntegrationBridge] Displaying remote cursor for user:', remoteUserId);
        
        // Convert block-based position to text position
        const textPosition = this.convertBlockPositionToTextPosition(caretAfter);
        
        // Update remote cursor display with actual user ID
        this.updateRemoteCursorDisplay(textPosition, remoteUserId);
      }
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error processing remote caret position:', error);
    }
  }

  /**
   * Determine if server layout enhancement should be used
   * Based on client-first architecture principles from the design document
   */
  private shouldUseServerLayoutEnhancement(): boolean {
    // Enhancement scenarios from architecture document (Section 5.1.1):
    // - idle: 2+ seconds of no activity
    // - multiPage: Document > 1 page  
    // - export: PDF generation
    // - legal: Court formatting
    
    const isIdle = (Date.now() - this.getLastActivityTime()) > 2000;
    const isMultiPage = this.estimatePageCount() > 1;
    const isExportMode = this.isInExportMode();
    const isLegalMode = this.isInLegalMode();
    
    return isIdle || isMultiPage || isExportMode || isLegalMode;
  }

  /**
   * Process server layout enhancement (selective use per architecture)
   */
  private processServerLayoutEnhancement(layoutPlan: any, planHash: string): void {
    try {
      console.log('🎯 [CollaborationIntegrationBridge] Applying server layout enhancement:', {
        planHash,
        reason: this.getEnhancementReason()
      });
      
      // Cache server layout plan for potential use
      this.cacheServerLayoutPlan(layoutPlan, planHash);
      
      // Apply enhancement to canvas renderer (non-disruptive)
      this.canvasRenderer.setBackendLayoutPlan?.(layoutPlan);
      
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error processing server layout enhancement:', error);
    }
  }

  /**
   * Extract text content from blocks structure
   */
  private extractTextFromBlocks(blocksText: any): string {
    try {
      if (typeof blocksText === 'string') {
        return blocksText;
      }
      
      if (blocksText && typeof blocksText === 'object') {
        // Handle block-based structure (e.g., {p1: 'text content'})
        const textParts: string[] = [];
        for (const [blockId, content] of Object.entries(blocksText)) {
          if (typeof content === 'string') {
            textParts.push(content);
          }
        }
        return textParts.join('\n');
      }
      
      return '';
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error extracting text from blocks:', error);
      return '';
    }
  }

  /**
   * Convert block-based position to linear text position
   */
  private convertBlockPositionToTextPosition(caretAfter: any): number {
    try {
      // Simple implementation - can be enhanced based on actual block structure
      return caretAfter.at || 0;
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error converting block position:', error);
      return 0;
    }
  }

  /**
   * Update remote cursor display for collaboration
   */
  private updateRemoteCursorDisplay(position: number, userId: string): void {
    try {
      // Update remote cursor without affecting local editing
      // This integrates with existing remote cursor system
      if (this.canvasRenderer.updateRemoteCursor) {
        this.canvasRenderer.updateRemoteCursor(userId, { x: position * 7.2, y: 72, visible: true });
      }
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error updating remote cursor display:', error);
    }
  }

  /**
   * Emit layout updated event for other components
   */
  private emitLayoutUpdatedEvent(): void {
    try {
      console.log('📐 [CollaborationIntegrationBridge] Layout updated event received');
      // This maintains compatibility with existing event system
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error emitting layout updated event:', error);
    }
  }

  // Helper methods for enhancement decision logic
  private getLastActivityTime(): number {
    return (this.documentEngine as any).getLastActivityTime?.() || Date.now();
  }

  private isInExportMode(): boolean {
    return (this.documentEngine as any).isInExportMode?.() || false;
  }

  private isInLegalMode(): boolean {
    return (this.documentEngine as any).isInLegalMode?.() || false;
  }

  private getEnhancementReason(): string {
    if ((Date.now() - this.getLastActivityTime()) > 2000) return 'idle';
    if (this.estimatePageCount() > 1) return 'multiPage';
    if (this.isInExportMode()) return 'export';
    if (this.isInLegalMode()) return 'legal';
    return 'unknown';
  }

  private cacheServerLayoutPlan(layoutPlan: any, planHash: string): void {
    try {
      // Cache server layout plan for potential future use
      (this as any).cachedServerLayout = { layoutPlan, planHash, timestamp: Date.now() };
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error caching server layout plan:', error);
    }
  }

  private estimatePageCount(): number {
    try {
      const text = this.documentEngine.getText();
      const wordsPerPage = 250; // Legal document standard
      const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;
      return Math.ceil(wordCount / wordsPerPage);
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error estimating page count:', error);
      return 1;
    }
  }

  /**
   * FIX 1: Check if cursor update is from current user (echo from server)
   */
  private isOwnCursorUpdate(caretAfter: any, eventData?: any): boolean {
    try {
      // Check if this cursor position matches our local caret position
      const localCaretPosition = this.documentEngine.getCaretPosition();
      const remoteCaretPosition = this.convertBlockPositionToTextPosition(caretAfter);
      
      // If positions match, this is likely an echo of our own cursor
      if (Math.abs(localCaretPosition - remoteCaretPosition) <= 1) {
        return true;
      }
      
      // Check if the event is from our own operation
      if (eventData?.opId && this.isOwnOperation(eventData.opId)) {
        return true;
      }
      
      // Check if there's no actual remote user associated with this cursor
      const remoteUserId = this.extractRemoteUserId(eventData);
      if (!remoteUserId) {
        return true; // No remote user = likely our own echo
      }
      
      return false;
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error checking own cursor update:', error);
      return true; // Default to filtering out on error
    }
  }

  /**
   * FIX 2: Extract actual user ID from collaboration event
   */
  private extractRemoteUserId(eventData?: any): string | null {
    try {
      // Try multiple possible user ID fields from collaboration events
      const userId = eventData?.userId || 
                    eventData?.user?.id || 
                    eventData?.author?.id ||
                    eventData?.collaborator?.id ||
                    eventData?.sessionId ||
                    null;
      
      if (userId && typeof userId === 'string') {
        return userId;
      }
      
      return null;
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error extracting remote user ID:', error);
      return null;
    }
  }

  /**
   * FIX 3: Check if user ID represents the current user
   */
  private isCurrentUser(userId: string): boolean {
    try {
      // Get current user information from various possible sources
      const currentUserId = this.getCurrentUserId();
      const currentSessionId = this.getCurrentSessionId();
      
      // Check if the user ID matches current user or session
      return userId === currentUserId || 
             userId === currentSessionId ||
             userId === 'current-user' ||
             userId === 'local-user';
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error checking current user:', error);
      return true; // Default to treating as current user on error
    }
  }

  /**
   * Check if operation ID belongs to current user
   */
  private isOwnOperation(opId: string): boolean {
    try {
      // Check if this operation ID was generated by us
      // Operation IDs typically contain session/user information
      return opId.includes(this.getCurrentSessionId() || 'unknown');
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error checking own operation:', error);
      return false;
    }
  }

  /**
   * Get current user ID from various sources
   */
  private getCurrentUserId(): string | null {
    try {
      // Try to get user ID from document engine, WebSocket client, or other sources
      return (this.wsClient as any)?.userId || 
             (this.documentEngine as any)?.getCurrentUserId?.() ||
             localStorage.getItem('currentUserId') ||
             null;
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error getting current user ID:', error);
      return null;
    }
  }

  /**
   * Get current session ID from WebSocket or other sources
   */
  private getCurrentSessionId(): string | null {
    try {
      return (this.wsClient as any)?.socketId || 
             (this.wsClient as any)?.sessionId ||
             sessionStorage.getItem('collaborationSessionId') ||
             null;
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error getting current session ID:', error);
      return null;
    }
  }

  /**
   * Handle EdgeDocs initial events (document initialization)
   */
  private async handleEdgeDocsInitial(eventData: any): Promise<void> {
    try {
      console.log('🎯 [CollaborationIntegrationBridge] EdgeDocs initial event processed:', {
        hasBlocksText: !!eventData.blocksText,
        hasLayoutPlan: !!eventData.layoutPlan,
        planHash: eventData.planHash,
        version: eventData.version,
        documentId: eventData.documentId
      });

      // FIXED: Process initial document content just like updates
      // This was the missing piece causing blank documents on page load
      
      // Process initial document content
      if (eventData.blocksText) {
        console.log('📄 [CollaborationIntegrationBridge] Loading initial document content');
        await this.processRemoteDocumentContent(eventData.blocksText);
      }

      // Process initial caret position if provided
      if (eventData.caretAfter) {
        this.processRemoteCaretPosition(eventData.caretAfter, eventData);
      }

      // CLIENT-FIRST ARCHITECTURE: Disable all server layout processing to prevent conflicts
      console.log('📐 [CollaborationIntegrationBridge] DISABLING server layout - pure client-side rendering only');
      // Skip all server layout processing and events to eliminate dual rendering conflicts
      
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error handling EdgeDocs initial:', error);
    }
  }

  /**
   * Handle incoming collaboration events from existing system
   */
  private handleCollaborationEvent(eventData: any): void {
    try {
      const eventType = eventData.event_type || eventData.type || eventData.__eventType;
      console.log('🔍 [CollaborationIntegrationBridge] Handling collaboration event:', {
        eventType,
        hasData: !!eventData?.data,
        currentBaselineAcked: this.baselineAcked,
        currentSessionReady: this.sessionReady
      });
      
      // ONLY treat 'initial' events as baseline ack - be more strict
      if (!this.baselineAcked && eventType === 'initial') {
        this.baselineAcked = true;
        const serverVersion = eventData?.data?.serverVersion ?? eventData?.serverVersion ?? 0;
        try { (this.documentEngine as any)?.setServerVersion?.(serverVersion); } catch {}
        console.log('✅ [CollaborationIntegrationBridge] Baseline acknowledged via INITIAL event');
        this.markSessionReady();
      } else if (!this.baselineAcked) {
        console.warn('🚫 [CollaborationIntegrationBridge] Received non-initial event while waiting for baseline:', {
          eventType,
          baselineAcked: this.baselineAcked,
          sessionReady: this.sessionReady
        });
      }
      
      switch (eventType) {
        case 'document-content-changed':
          this.handleRemoteContentChange(eventData);
          break;
          
        case 'user-cursor-moved':
          this.handleRemoteCursorMove(eventData);
          break;
          
        case 'user-joined':
          this.handleUserJoined(eventData);
          break;
          
        case 'user-left':
          this.handleUserLeft(eventData);
          break;
          
        case 'comment-added':
          this.handleCommentAdded(eventData);
          break;
          
        case 'change-tracked':
          this.handleChangeTracked(eventData);
          break;
          
        case 'edgedocs:update':
          this.handleEdgeDocsUpdate(eventData).catch(error => {
            console.error('[CollaborationIntegrationBridge] Error in handleEdgeDocsUpdate:', error);
          });
          break;
          
        case 'edgedocs:initial':
          this.handleEdgeDocsInitial(eventData).catch(error => {
            console.error('[CollaborationIntegrationBridge] Error in handleEdgeDocsInitial:', error);
          });
          break;
          
        default:
          console.log(`[CollaborationIntegrationBridge] Unhandled event: ${eventType}`);
      }
      
    } catch (error) {
      console.error('[CollaborationIntegrationBridge] Error handling collaboration event:', error);
    }
  }

  /**
   * Handle remote content changes from other users
   */
  private handleRemoteContentChange(eventData: any): void {
    if (!eventData.data) return;
    
    const { operation, position, content, operation_id } = eventData.data;
    
    // Don't apply our own operations
    if (eventData.user_id === this.config?.attorney.id) return;
    
    console.log(`[CollaborationIntegrationBridge] Applying remote ${operation} from user ${eventData.user_id}`);
    
    // Convert to operation format and apply
    const remoteOperation: Operation = {
      id: operation_id,
      type: operation,
      version: 0, // Will be set by document engine
      attorney: { 
        id: eventData.user_id, 
        name: 'Remote User', 
        email: 'remote@example.com',
        barNumber: 'unknown',
        firmName: 'Remote Firm',
        role: 'associate',
        seniority: 1
      },
      timestamp: new Date(eventData.timestamp),
      position: position.start,
      content: content || '',
      start: position.start,
      end: position.end
    };
    
    // Apply remote operation without triggering new collaboration events
    this.documentEngine.applyRemoteOperation(remoteOperation, { 
      id: eventData.user_id, 
      name: 'Remote User' 
    });
  }

  /**
   * Handle remote cursor movements
   */
  private handleRemoteCursorMove(eventData: any): void {
    if (!eventData.data) return;
    
    // Update canvas renderer with remote cursor position
    const cursorData = eventData.data.cursor_position;
    
    // Emit cursor update for rendering
    this.canvasRenderer.updateRemoteCursor?.(eventData.user_id, {
      x: cursorData.x || 0,
      y: cursorData.y || 0,
      visible: true
    });
    
    console.log(`[CollaborationIntegrationBridge] Updated remote cursor for user ${eventData.user_id}`);
  }

  /**
   * Handle user joined events
   */
  private handleUserJoined(eventData: any): void {
    console.log(`[CollaborationIntegrationBridge] User joined: ${eventData.data?.user?.name || eventData.user_id}`);
    
    // Log collaboration event (emit is private)
    console.log(`[CollaborationIntegrationBridge] Collaborator joined: ${eventData.data?.user?.name || eventData.user_id}`);
  }

  /**
   * Handle user left events
   */
  private handleUserLeft(eventData: any): void {
    console.log(`[CollaborationIntegrationBridge] User left: ${eventData.user_id}`);
    
    // Remove remote cursor
    this.canvasRenderer.removeRemoteCursor?.(eventData.user_id);
    
    // Log collaboration event (emit is private)
    console.log(`[CollaborationIntegrationBridge] Collaborator left: ${eventData.user_id}`);
  }

  /**
   * Handle comment added events
   */
  private handleCommentAdded(eventData: any): void {
    console.log(`[CollaborationIntegrationBridge] Comment added by ${eventData.user_id}`);
    
    // Log comment event (emit is private)
    console.log(`[CollaborationIntegrationBridge] Comment added by ${eventData.data?.author?.name || eventData.user_id}`);
  }

  /**
   * Handle change tracked events
   */
  private handleChangeTracked(eventData: any): void {
    console.log(`[CollaborationIntegrationBridge] Change tracked by ${eventData.user_id}`);
    
    // Log change tracking event (emit is private)
    console.log(`[CollaborationIntegrationBridge] Change tracked by ${eventData.data?.author?.name || eventData.user_id}`);
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Send cursor position update
   */
  sendCursorUpdate(position: number): void {
    if (!this.wsClient || !this.config) return;
    
    const charPosition = this.canvasRenderer?.getCharacterPosition?.(position);
    
    if (charPosition) {
          this.wsClient.sendCursor(this.config.documentId, {
      offset: position,
      x: charPosition.x,
      y: charPosition.y
    });
    }
  }

  /**
   * Send selection change update
   */
  sendSelectionUpdate(start: number, end: number): void {
    if (!this.wsClient || !this.config) return;
    
    this.wsClient.sendCursor(this.config.documentId, {
      offset: start
    }, {
      anchor: { key: 'document', offset: start, type: 'text' },
      focus: { key: 'document', offset: end, type: 'text' },
      collapsed: start === end,
      direction: start <= end ? 'forward' : 'backward'
    });
  }

  /**
   * Get connection status
   */
  getConnectionStatus(): {
    connected: boolean;
    websocketReady: boolean;
    documentRoom: string | null;
  } {
    return {
      connected: this.isConnected,
      websocketReady: this.wsClient !== null,
      documentRoom: this.config?.documentId || null
    };
  }

  /**
   * Force sync with server
   */
  async forceSync(): Promise<void> {
    if (!this.wsClient || !this.config) return;
    
    // Request document state sync using existing method
    this.wsClient.getInitial(this.config.documentId);
  }

  /**
   * Process server layout plan to preserve HarfBuzz typography benefits
   */
  private processServerLayoutPlan(layoutPlan: any): void {
    try {
      console.log('🔤 [HARFBUZZ_INTEGRATION] Processing server layout plan:', {
        hasPages: !!layoutPlan.pages,
        pageCount: layoutPlan.pages?.length || 0,
        hasFontUsageHints: !!layoutPlan.fontUsageHints
      });

      // 🎯 CRITICAL FIX: Skip collaboration layout plans if they use different DPI than SyncManager
      // The collaboration system uses default DPI 96, but SyncManager may have custom DPI (e.g., 300)
      const collaborationDPI = layoutPlan.pageSetup?.dpi || 96;
      const isDefaultDPI = collaborationDPI === 96;
      
      if (!isDefaultDPI) {
        console.log('🔤 [HARFBUZZ_INTEGRATION] Using collaboration layout plan with custom DPI:', collaborationDPI);
      } else {
        // Check if this is a low-DPI layout plan that might conflict with SyncManager's high-DPI setting
        const paperWidth = layoutPlan.pages?.[0]?.paperWidth || 0;
        const isLowDPILayout = paperWidth < 1000; // 816px = 96 DPI, 2550px = 300 DPI
        
        if (isLowDPILayout) {
          console.log('🚨 [HARFBUZZ_INTEGRATION] SKIPPING low-DPI collaboration layout plan to preserve SyncManager high-DPI settings');
          console.log('🚨 [HARFBUZZ_INTEGRATION] Collaboration paperWidth:', paperWidth, 'vs expected high-DPI: >2000px');
          return; // Skip applying this layout plan
        }
      }

      // Apply server layout plan to canvas renderer if available
      if (this.canvasRenderer && layoutPlan.pages) {
        console.log('🔤 [HARFBUZZ_INTEGRATION] Applying HarfBuzz layout plan to canvas renderer');
        this.canvasRenderer.setBackendLayoutPlan?.(layoutPlan);
        this.canvasRenderer.requestRender();
        console.log('✅ [HARFBUZZ_INTEGRATION] Server layout plan applied successfully');
      } else {
        console.warn('⚠️ [HARFBUZZ_INTEGRATION] Cannot apply layout plan - missing canvas renderer or pages');
      }
    } catch (error) {
      console.error('❌ [HARFBUZZ_INTEGRATION] Error processing server layout plan:', error);
    }
  }

  /**
   * Disconnect from collaboration
   */
  disconnect(): void {
    if (this.wsClient) {
      this.wsClient.disconnect();
      this.wsClient = null;
    }
    
    this.isConnected = false;
    this.config = null;
    
    console.log('[CollaborationIntegrationBridge] Disconnected from collaboration');
  }

  /**
   * Destroy integration bridge
   */
  destroy(): void {
    this.disconnect();
    console.log('[CollaborationIntegrationBridge] Integration bridge destroyed');
  }
}
